<template>
  <div class="purchase-details fade-in">
    <!-- Header Section -->
    <ProductDetailsHeader
        title="Direct Purchase Details"
        icon="cart-plus"
        :main="purchase"
        :header-details="headerDetails"
        :files="purchase.files"
    >
      <template #header-actions>
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <button
              v-if="purchase.status === 'submitted' && purchase.invoice === null"
              class="btn btn-success"
              @click="addInvoiceModal = true;">
            <i class="bi bi-file-earmark-plus me-1"></i> Add Invoice
          </button>

          <div v-if="purchase.status !== 'complete' && purchaseProducts.length > 0">
            <select
                class="form-select status-select w-auto"
                v-model="purchaseStatus"
                @change="confirmDialog(
                    getConfirmTitle,
                    updatePurchaseStatus,
                    'Are you sure you want to change direct purchase status to ' + purchaseStatus
                )" data="">
              <option value="" disabled selected>Change Status</option>
              <option v-if="purchase.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
              <option v-if="purchase.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
              <option v-if="purchase.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
            </select>
          </div>
        </div>
      </template>
    </ProductDetailsHeader>

    <!-- Available Products Section (when status is pending) -->
    <StoreProducts
        v-if="purchase.status === 'pending'"
        :per-page="5"
        :marked-products="purchaseProducts.data"
        @row-click="populateAddProduct"
    />

    <!-- Purchase Products Section -->
    <ProductDetailsPages
        title="Direct Purchase"
        :loading="loading"
        :detail-products="purchaseProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="purchase"
        :additional-fields="[{ name: 'sell', label: 'SELLING PRICE', format: true }]"
        @edit-product="populatePurchaseEdit"
        @delete-product="deletePurchaseProduct"
    >
      <template #footer-actions>
        <div v-if="purchase.status === 'submitted'">
          <button
              v-if="purchase.invoice === null"
              class="btn btn-success"
              @click="addInvoiceModal = true;">
            <i class="bi bi-file-earmark-plus me-1"></i> Add Invoice
          </button>
        </div>
        <div v-if="purchase.status !== 'complete' && purchaseProducts.data.length > 0">
          <select
              class="form-select status-select w-auto"
              v-model="purchaseStatus"
              @change="confirmDialog(
                  getConfirmTitle,
                  updatePurchaseStatus,
                  'Are you sure you want to change direct purchase status to ' + purchaseStatus
                )" data="">
            <option value="" disabled selected>Change Status</option>
            <option v-if="purchase.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
            <option v-if="purchase.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
            <option v-if="purchase.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
          </select>
        </div>
      </template>
    </ProductDetailsPages>

    <!-- Add Invoice Modal -->
    <Modal :open="addInvoiceModal" @closed="addInvoiceModal = false">
      <div class="modal-header">
        <h5 class="modal-title m-0">Add Supplier Document</h5>
        <button @click="addInvoiceModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-12">
              <label for="name" class="form-label fw-bold h5 mt-3">Invoice/Receipt No:</label>
              <input type="text" class="form-control form-control-lg" v-model="invoice.invoice" required>
            </div>
            <div class="form-group col-md-12">
              <label for="quantity" class="form-label fw-bold h5 mt-3">Invoice/Receipt File:</label>
              <input class="form-control form-control-lg" type="file" @input="handleFileUpload" name="docs" accept=".jpeg,.png,.jpg,.pdf">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-1 border-info border-opacity-75 mt-3">
        <button type="button" class="btn btn-dark text-uppercase me-3" data-dismiss="modal" @click="addInvoiceModal = false">Cancel</button>
        <button type="button" class="btn btn-primary text-uppercase" @click="addInvoice">Submit</button>
      </div>
    </Modal>
    <!-- Add Invoice Modal End -->

    <!-- Add Purchase Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0 d-flex align-items-center" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Purchase
        </h5>
        <h5 class="modal-title m-0 d-flex align-items-center" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Purchased Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <form @submit.prevent="">
          <!-- Product Information Section -->
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="name" class="form-label fw-bold">Product Name:</label>
                  <input type="text" class="form-control" :value="selectedProduct.name" disabled>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="sku" class="form-label fw-bold">SKU:</label>
                  <input type="text" class="form-control" :value="selectedProduct.sku" disabled>
                </div>
              </div>
            </div>
          </div>

          <!-- Purchase Details Section -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-light py-2">
              <h6 class="mb-0"><i class="bi bi-cart-plus me-2"></i>Purchase Details</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="quantity" class="form-label fw-bold">Quantity:</label>
                    <div class="input-group">
                      <input type="number" class="form-control" v-model="purchaseDetails.quantity" min="1" required>
                      <span class="input-group-text" v-if="selectedProduct.unit">{{ selectedProduct.unit.name }}</span>
                    </div>
                    <small class="form-text text-muted">Current stock: {{ selectedProduct.stock || 0 }}</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="expiry_date" class="form-label fw-bold">Expiry Date (Optional):</label>
                    <input type="date" class="form-control" v-model="purchaseDetails.expiry_date">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="price" class="form-label fw-bold">Buying Price:</label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" v-model="purchaseDetails.price" min="0" step="0.01" required>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="sell" class="form-label fw-bold">Selling Price:</label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" v-model="purchaseDetails.sell" min="0" step="0.01" required>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="form-group mb-3">
                    <label for="comment" class="form-label fw-bold">Comment (Optional):</label>
                    <input type="text" class="form-control" v-model="purchaseDetails.comment" placeholder="Add a note about this purchase">
                  </div>
                </div>
              </div>

              <!-- Summary Section -->
              <div class="row mt-3">
                <div class="col-12">
                  <div class="card border-0 shadow-sm bg-light">
                    <div class="card-body p-3">
                      <!-- Total Purchase Value -->
                      <div class="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                        <div>
                          <i class="bi bi-cash-stack text-primary me-2"></i>
                          <strong>Total Purchase Value</strong>
                        </div>
                        <div class="fs-4 fw-bold text-primary">{{ formatNumber(purchaseDetails.quantity * purchaseDetails.price) }}</div>
                      </div>

                      <!-- Pricing Information Grid -->
                      <div class="row g-3">
                        <!-- Current Store Price -->
                        <div class="col-md-6">
                          <div class="p-3 bg-white rounded border">
                            <div class="small text-muted mb-1">
                              <i class="bi bi-shop me-1"></i>Current Store Price
                              <span class="fs-5 fw-semibold text-dark float-end">{{ formatNumber(selectedProduct.actualSellingPrice) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Suggested Price -->
                        <div class="col-md-6">
                          <div class="p-3 bg-white rounded border border-info">
                            <div class="small text-muted mb-1">
                              <i class="bi bi-lightbulb me-1"></i>
                              <span class="fs-7 fw-semibold text-success">({{ expectedMargin }}% margin)</span>
                              <span class="fs-5 fw-semibold text-info float-end">{{ formatNumber(suggestedPrice) }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Actual Margin -->
                        <div class="col-md-6">
                          <div class="p-3 bg-white rounded border" :class="actualMargin >= expectedMargin ? 'border-success' : 'border-warning'">
                            <div class="small text-muted mb-1">
                              <i class="bi bi-percent me-1"></i>Actual Margin
                              <span class="fs-5 fw-semibold float-end" :class="actualMargin >= expectedMargin ? 'text-success' : 'text-warning'">
                                {{ actualMargin }}%
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Profit Per Unit -->
                        <div class="col-md-6">
                          <div class="p-3 bg-white rounded border">
                            <div class="small text-muted mb-1">
                              <i class="bi bi-currency-dollar me-1"></i>Profit Per Unit
                              <span class="fs-5 fw-semibold text-success float-end">{{ formatNumber(profitPerUnit) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addPurchaseProduct">
          <i class="bi bi-plus-circle me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updatePurchaseProduct(purchaseDetails.id)">
          <i class="bi bi-check-circle me-1"></i> Update Product
        </button>
      </div>
    </Modal>
    <!-- Add Purchase Modal End -->
  </div>

</template>

<script>
import directPurchaseService from "@/api/directPurchaseService";
import confirmDialog  from "@/utils/confirmDialog";
import formatNumber from "@/utils/formatNumber";
import notify from "@/utils/notify";
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";
import StoreProducts from "@/components/store/StoreProducts.vue";

export default {
  name: "DirectPurchaseDetails",

  components: { ProductDetailsHeader, ProductDetailsPages, StoreProducts },

  data(){
    return {
      profile: {},
      store: {},
      purchaseStatus: '',
      purchase: {
        supplier: {},
        user: {},
        store: {}
      },
      invoice: {
        direct_purchase_id: this.$route.params.id,
        invoice: '',
        file: '',
      },
      purchaseDetails: {
        direct_purchase_id: this.$route.params.id,
        product_id: '',
        quantity: 1,
        price: '',
        sell: '',
        expiry_date: '',
        comment: '',
      },
      selectedProduct: {
        category: {},
        actualSellingPrice: 0
      },
      purchaseProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      loading: true,
      addInvoiceModal: false,
      addProductModal: false,
      addProductMode: 'add',
      selectedFile: null,
    }
  },

  methods: {
    confirmDialog,
    formatNumber,
    async getPurchaseDetails() {
      this.purchaseProducts = await directPurchaseService.getPurchaseDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getPurchaseDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getPurchaseDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getPurchaseDetails();
    },
    populateAddProduct(storeProduct) {
      this.addProductModal = true;
      this.addProductMode = 'add';
      this.selectedProduct.name = storeProduct.product.name
      this.selectedProduct.sku = storeProduct.product.sku
      this.selectedProduct.stock = storeProduct.stock
      this.selectedProduct.unit = storeProduct.product.unit
      this.selectedProduct.category = storeProduct.product.category || {}
      this.selectedProduct.actualSellingPrice = storeProduct.price
      this.purchaseDetails.product_id = storeProduct.product_id
      this.purchaseDetails.price = storeProduct.product.buy
      this.purchaseDetails.sell = storeProduct.price
    },
    async addPurchaseProduct() {
      let response = await directPurchaseService.createPurchaseDetails(this.purchaseDetails)
      if (response) {
        this.addProductModal = false
        await this.getPurchaseDetails()
      }
    },
    async updatePurchaseProduct() {
      let response = await directPurchaseService.updatePurchaseDetails(this.purchaseDetails, this.purchaseDetails.id)
      if (response) {
        this.addProductModal = false
        await this.getPurchaseDetails()
      }
    },
    populatePurchaseEdit(purchase) {
      this.purchaseDetails.direct_purchase_id = purchase.direct_purchase_id
      this.purchaseDetails.id = purchase.id
      this.selectedProduct.name = purchase.product.name
      this.selectedProduct.sku = purchase.product.sku
      this.selectedProduct.stock = purchase.stock
      this.selectedProduct.unit = purchase.product.unit
      this.selectedProduct.category = purchase.product.category || {}
      this.selectedProduct.actualSellingPrice = purchase.store_product?.price || 0
      this.purchaseDetails.product_id = purchase.product_id
      this.purchaseDetails.price = purchase.price
      this.purchaseDetails.sell = purchase.sell
      this.purchaseDetails.quantity = purchase.quantity
      this.purchaseDetails.expiry_date = purchase.expiry_date
      this.purchaseDetails.comment = purchase.comment
      this.addProductModal = true;
      this.addProductMode = 'edit';
    },
    async deletePurchaseProduct(id) {
      let response = await directPurchaseService.deletePurchaseDetails(id)
      if (response) {
        await this.getPurchaseDetails()
      }
    },
    async updatePurchaseStatus() {
      if (this.purchase.invoice === null && this.purchaseStatus === 'complete') {
        notify.alert.info('Please add invoice before approving purchase')
      } else {
        await directPurchaseService.changePurchaseStatus({status: this.purchaseStatus}, this.$route.params.id)
      }
    },
    handleFileUpload(file) {
      this.selectedFile = file
    },
    addInvoice : async function() {
      if (this.selectedFile === null) {
        notify.alert.error('Please select a file')
      } else {
        let fileReader = this.processFile(this.selectedFile)
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            this.invoice.file = fileReader.result
          let response = await directPurchaseService.addSupplierInvoice(this.invoice)
          if (response)
            window.location.reload()
        }
      }
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
  },

  computed: {
    calculateProfitMargin() {
      if (!this.purchaseDetails.price || this.purchaseDetails.price <= 0) return 0;
      if (!this.purchaseDetails.sell) return 0;

      const buyingPrice = parseFloat(this.purchaseDetails.price);
      const sellingPrice = parseFloat(this.purchaseDetails.sell);
      const profit = sellingPrice - buyingPrice;
      const margin = (profit / buyingPrice) * 100;

      return Math.round(margin * 100) / 100; // Round to 2 decimal places
    },
    actualMargin() {
      if (!this.purchaseDetails.price || this.purchaseDetails.price <= 0) return 0;
      if (!this.purchaseDetails.sell) return 0;

      const buyingPrice = parseFloat(this.purchaseDetails.price);
      const sellingPrice = parseFloat(this.purchaseDetails.sell);
      const profit = sellingPrice - buyingPrice;
      const margin = (profit / buyingPrice) * 100;

      return Math.round(margin * 100) / 100;
    },
    expectedMargin() {
      if (this.selectedProduct.category?.sale_margin_percentage) {
        return this.selectedProduct.category.sale_margin_percentage;
      }
      return 20; // Default margin
    },
    suggestedPrice() {
      if (!this.purchaseDetails.price) return 0;
      const buyingPrice = parseFloat(this.purchaseDetails.price);
      const margin = this.expectedMargin;
      return Math.ceil(((buyingPrice * (margin/100)) + buyingPrice));
    },
    profitPerUnit() {
      if (!this.purchaseDetails.price || !this.purchaseDetails.sell) return 0;
      const buyingPrice = parseFloat(this.purchaseDetails.price);
      const sellingPrice = parseFloat(this.purchaseDetails.sell);
      return sellingPrice - buyingPrice;
    },
    getConfirmTitle() {
      switch (this.purchaseStatus) {
        case 'submitted':
          return 'Submit Direct Purchase'
        case 'pending':
          return 'Edit Direct Purchase'
        case 'complete':
          return 'Approve Direct Purchase'
      }
    },
    headerDetails() {
      return [
        { icon: 'person', label: 'Supplier', key: 'supplier.name' },
        { icon: 'card-text', label: 'Invoice/Receipt', key: 'invoice' },
      ];
    },
  },

  created: async function () {
    this.purchase = await directPurchaseService.getPurchase(this.$route.params.id)
    await this.getPurchaseDetails()
  },

  watch: {
    addProductModal: function () {
      if (!this.addProductModal) {
        this.purchaseDetails = {
          direct_purchase_id: this.$route.params.id,
          product_id: '',
          quantity: '',
          price: '',
          sell: '',
          expiry_date: '',
          comment: ''
        }
        this.selectedProduct = { category: {}, actualSellingPrice: 0 }
        this.addProductMode = 'add'
      }
    }
  },
}
</script>

<style scoped>
</style>
